import { Flex } from "@chakra-ui/react";
import CDS from "design-system";
import RecipientTag from "components/Tag/ResourceTargetTag/RecipientTag";
import { UserId } from "features/User/models";
import { HorizontalGreyBlackField } from "components/DataDisplay/GreyBlackField";
import { longAttributeStyles } from "components/DataDisplay/styles";
import useTargetName from "hooks/useTargetName";
import DisplayResourceTargetsColumn from "features/Resources/SharedTemplate/Components/DisplayResourceTargetsColumn";
import ResourceTargetAvatar from "features/Resources/SharedTemplate/Components/ResourceTargetAvatar";
import { Message } from "../models";

type MessageRecipient = {

  id: string;

  type: "User" | "OnboardingJourneyNetworkRelation" | "MessageServiceChannel";
};

function MessageRecipientAvatar({ recipient }: { recipient: MessageRecipient }) {
  const { name, role } = useTargetName({
    resourceId: recipient.id,
    resourceType: recipient.type,
    includeChannelHash: true,
  });
  return <ResourceTargetAvatar name={name || ""} roleName={role} />;
}

export function DisplayRecipientsColumn({ message }: { message: Message }) {
  return (
    <Flex align="center" gap="1">
      <DisplayResourceTargetsColumn<MessageRecipient>
        items={[{ id: message.recipientId, type: message.recipientType }]}
        renderTag={({ id, type }: MessageRecipient) => (
          <RecipientTag key={id} resourceId={id} resourceType={type} disabled />
        )}
        renderAvatar={(recipient: MessageRecipient) => <MessageRecipientAvatar recipient={recipient} />}
        getKey={(recipient: MessageRecipient) => recipient.id}
        emptyLabel="No Recipients"
      />
    </Flex>
  );
}

export function DisplayRecipients({ message }: { message: Message }) {
  return <DisplayRecipientsColumn message={message} />;
}

export function DisplayRecipientsValue({ message }: { message: Message }) {
  const { name } = useTargetName({
    resourceId: message?.recipientId,
    resourceType: message?.recipientType,
    includeChannelHash: true,
  });
  return name;
}

interface ShowRecipientProps {
  recipientId: UserId  ;
  recipientType: "User" | "MessageServiceChannel";
  label: string;
}

export function ShowRecipient({ recipientId, recipientType, label, ...rest }: ShowRecipientProps) {
  return (
    <HorizontalGreyBlackField
      label={label}
      labelContainerProps={longAttributeStyles.labelContainerProps}
    >
      <Flex align="center" gap="1" {...rest}>
        <RecipientTag resourceId={recipientId} resourceType={recipientType} disabled />
      </Flex>
    </HorizontalGreyBlackField>
  );
}

interface ShowSenderProps {
  senderId?: UserId;
  label: string;
}

type MessageSender = {

  id?: string;

  type: "User" | "Bot";
};

function MessageSenderAvatar({ sender }: { sender: MessageSender }) {
  const { name, role } = useTargetName({
    resourceId: sender.id || "",
    resourceType: "User",
    options: { enabled: sender.type === "User" },
  });

  if (sender.type === "Bot") {
    return <ResourceTargetAvatar name="Camino Bot" src="favico.ico" />;
  }

  return <ResourceTargetAvatar name={name || ""} roleName={role} />;
}

export function DisplaySenderColumn({ senderId }: { senderId?: UserId }) {
  const sender: MessageSender = senderId
    ? { id: senderId, type: "User" }
    : { type: "Bot" };

  return (
    <Flex align="center" gap="1">
      <DisplayResourceTargetsColumn<MessageSender>
        items={[sender]}
        renderTag={(s: MessageSender) => {
          if (s.type === "Bot") {
            return <CDS.Text14 key="bot">Camino Bot</CDS.Text14>;
          }
          return <RecipientTag key={s.id} resourceId={s.id || ""} resourceType="User" disabled />;
        }}
        renderAvatar={(s: MessageSender) => <MessageSenderAvatar sender={s} />}
        getKey={(s: MessageSender) => s.id || "bot"}
        emptyLabel="No Sender"
      />
    </Flex>
  );
}

export function ShowSender({ senderId, label, ...rest }: ShowSenderProps) {
  return (
    <HorizontalGreyBlackField
      label={label}
      labelContainerProps={longAttributeStyles.labelContainerProps}
    >
      <Flex align="center" gap="1" {...rest}>
        {senderId ? (
          <RecipientTag resourceId={senderId} resourceType="User" disabled />
        ) : (
          <CDS.Text14>Camino Bot</CDS.Text14>
        )}
      </Flex>
    </HorizontalGreyBlackField>
  );
}
